import {useAuth} from "../../utils/hooks/useAuth";
import {useConfirm} from "material-ui-confirm";
import {useNavigate, useParams} from "react-router-dom";
import {useNotification} from "../../utils/hooks/useNotification";
import useSWR from "swr";
import {deleteFetcher, getFetcher, putFetcher} from "../../utils/api/fetchers";
import {useForm} from "react-hook-form";
import {zodResolver} from "@hookform/resolvers/zod";
import React, {useEffect, useState} from "react";
import UserRole from "../../schemas/user/roles";
import Loading from "../../components/status/Loading";
import ErrorMessage from "../../components/status/ErrorMessage";
import {Box, Button, Paper, Stack, TextField, Typography} from "@mui/material";
import {Club, clubSchema} from "../../schemas/club/clubSchema";

const ClubDetails: React.FC = () => {
    const {hasPermission} = useAuth();
    const confirm = useConfirm();
    const navigate = useNavigate();
    const {clubId} = useParams<{ clubId: string }>();
    const {showNotification} = useNotification();
    const {data: club, isLoading, error, mutate} = useSWR<Club>(`/clubs/${clubId}`, getFetcher);
    const {
        register,
        handleSubmit,
        reset,
        formState: {errors, isDirty},
    } = useForm<Club>({
        defaultValues: club,
        resolver: zodResolver(clubSchema),
    });

    const [editMode, setEditMode] = useState<boolean>(false);
    const readOnly = !hasPermission(UserRole.COMMODORE);
    const noEdit = readOnly || !editMode;

    useEffect(() => {
        if (club) {
            reset(club);
        }
    }, [club, reset]);

    const onSubmit = async (data: Club) => {
        if (!isDirty) {
            showNotification("Nebyly provedeny žádné změny", "info");
            setEditMode(false);
            return;
        }

        putFetcher(`/clubs/${clubId}`, data)
            .then(() => {
                showNotification("Klub byl úspěšně upraven", "success");
                mutate();
                setEditMode(false);
            })
            .catch((error) => {
                if (error.response?.status === 409) {
                    showNotification("Klub s tímto jménem již existuje", "error");
                } else {
                    showNotification("Nepodařilo se upravit klub", "error");
                }
            });
    };

    const handleDelete = () => {
        confirm({
            description: `Opravdu chcete smazat tento klub?`,
            title: "Smazat klub",
            confirmationText: "Smazat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                deleteFetcher(`/clubs/${clubId}`).then(() => {
                    showNotification('Klub byl smazán', 'success');
                    navigate('/clubs');
                }).catch(() => {
                    showNotification('Klub se nepodařilo smazat', 'error');
                });
            })
            .catch(() => {
            });
    };

    if (isLoading || !club) return <Loading/>;
    if (error) return <ErrorMessage/>

    return (
        <Paper elevation={3} sx={{padding: 4, maxWidth: 600, margin: '0 auto'}}>
            <Typography variant="h4" gutterBottom>
                Detail klubu
            </Typography>
            <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
                <Stack spacing={3}>
                    <TextField
                        autoFocus
                        fullWidth
                        label="Jméno"
                        variant="standard"
                        required
                        {...register('name')}
                        error={!!errors.name}
                        helperText={errors.name?.message}
                        InputProps={{readOnly: noEdit}}
                    />
                    {!readOnly && (
                        editMode ? (
                            <Stack direction='row' spacing={2}>
                                <Button
                                    variant="contained"
                                    color="error"
                                    sx={{width: 250}}
                                    onClick={() => {
                                        setEditMode(false);
                                        reset(club);
                                    }}
                                >
                                    Zrušit změny
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleSubmit(onSubmit)}
                                >
                                    Uložit
                                </Button>
                            </Stack>
                        ) : (
                            <Stack direction='row' spacing={2}>
                                {hasPermission(UserRole.ADMIN) && (
                                    <Button
                                        variant="outlined"
                                        color="error"
                                        sx={{px: 10}}
                                        onClick={handleDelete}
                                    >
                                        Smazat
                                    </Button>
                                )}
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={() => setEditMode(true)}
                                >
                                    Upravit
                                </Button>
                            </Stack>
                        )
                    )}
                </Stack>
            </Box>
        </Paper>
    );
};

export default ClubDetails;